import { Component, Input, OnInit, SimpleChanges, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { IPermission } from '@core/interfaces/ipermission';
import { IStateDashboardConfiguration, IStateDashboardPermission, IStateDashboardTile } from '@core/interfaces/istate-dashboard';
import { LookupService } from '@core/services/lookup.service';
import { StateService } from '@core/services/state.service';
import { forkJoin, take } from 'rxjs';

@Component({
    selector: 'drdp-state-dashboard',
    templateUrl: './state-dashboard.component.html',
    styleUrls: ['./state-dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StateDashboardComponent implements OnInit {
  @Input() stateId?: number;
  @Input() permission?: IPermission;
  dashboardForm: FormGroup | any;
  dashboardTiles: IStateDashboardTile[] = [];
  configurations: IStateDashboardConfiguration[] = [];
  isSubmitting: boolean = false;
  stateDashboardEnabled: boolean = false;

  private fb = inject(FormBuilder);
  private lookupService = inject(LookupService);
  private stateService = inject(StateService);
  private router = inject(Router);

  ngOnInit(): void {
    this.lookupService.getStateDashboardTiles()
      .pipe(take(1))
      .subscribe((tiles: IStateDashboardTile[]) => {
        this.dashboardTiles = tiles;
      });

    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stateId']) this.initializeValues();
  }

  initializeForm(): void {
    this.dashboardForm = this.fb.group({
      enrollment: [false],
      ageGroup: [false],
      childByRace: [false],
      rating: [false],
      programByAgeGroup: [false],
      homeLanguage: [false],
      childByIep: [false],
      ageBasedOnDob: [false],
      genderDemographic: [false],
      childInSped: [false],
      disability: [false],
    });
  }

  initializeValues(): void {
    if (this.stateId && this.permission) {
      forkJoin([
        this.stateService.getDashboardConfigurations(this.stateId, this.permission.id),
        this.stateService.stateDashboardEnabled(this.stateId, this.permission.id)
      ]).pipe(take(1))
        .subscribe(([configs, stateDashboardEnabled]) => {
          this.configurations = configs;
          this.configurations.forEach(config => {
            this.dashboardForm.get(config.controlName).setValue(config.isActive);
          });
          this.stateDashboardEnabled = stateDashboardEnabled;
        });
    }
  }

  updateConfigurations(): void {
    Object.keys(this.dashboardForm.controls).forEach(key => {
      const config = this.configurations.find(c => c.controlName === key);

      if (config) {
        config.isActive = this.dashboardForm.get(key).value;
      } else if (this.dashboardForm.get(key).value) {
        this.configurations.push({
          id: 0,
          stateId: this.stateId ?? 0,
          controlName: key,
          stateDashboardTileId: this.dashboardTiles.find(t => t.controlName === key)?.id ?? 0,
          isActive: this.dashboardForm.get(key).value,
          stateDashboardPermissionId: this.configurations.find(c => c.stateDashboardPermissionId)?.stateDashboardPermissionId ?? 0
        });
      }
    });
  }

  onSubmit(): void {
    this.isSubmitting = true;
    if (this.dashboardForm.value) {
      this.updateConfigurations();

      this.stateService.updateDashboardConfigurations(this.configurations, this.stateId ?? 0, this.permission?.id ?? 0, this.stateDashboardEnabled)
        .pipe(take(1)).subscribe((res: boolean) => {
          if (res) {
            this.initializeValues();
            this.isSubmitting = false;
          }
        });
    }
  }

  onCancel(): void {
    this.router.navigate(['config/states']);
  }
}
